<template>
  <div
    ref="playerShell"
    class="job-approval-video-player"
    @click.stop
  >
    <video
      ref="player"
      class="job-approval-video-player__video"
      :class="videoClass"
      :style="videoStyle"
      :src="src"
      controlsList="nodownload"
      @click="handleVideoClick"
      @loadedmetadata="handleLoadedMetadata"
      @timeupdate="handleTimeUpdate"
      @play="syncPlayerState"
      @pause="syncPlayerState"
      @ended="syncPlayerState"
      @volumechange="syncPlayerState"
      @mousedown="$emit('video-mousedown', $event)"
      @mousemove="$emit('video-mousemove', $event)"
      @mouseup="$emit('video-mouseup', $event)"
      @mouseleave="$emit('video-mouseleave', $event)"
    ></video>

    <div
      class="job-approval-video-player__controls"
      @click.stop
    >
      <button
        type="button"
        class="job-approval-video-player__control-button"
        :aria-label="isPlaying ? 'Pause' : 'Play'"
        @click="togglePlayback"
      >
        <v-icon small>{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
      </button>

      <span class="job-approval-video-player__time-display">{{ currentTimeLabel }}</span>

      <input
        class="job-approval-video-player__seek-control"
        type="range"
        min="0"
        :max="duration || 0"
        step="0.01"
        :value="currentTime"
        aria-label="Seek"
        @input="seek"
      />

      <span class="job-approval-video-player__time-display">{{ durationLabel }}</span>

      <button
        type="button"
        class="job-approval-video-player__control-button"
        :aria-label="isMuted ? 'Unmute' : 'Mute'"
        @click="toggleMute"
      >
        <v-icon small>{{ volumeIcon }}</v-icon>
      </button>

      <button
        type="button"
        class="job-approval-video-player__control-button"
        :aria-label="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
        @click="toggleFullscreen"
      >
        <v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JobApprovalVideoPlayer',

  props: {
    src: {
      type: String,
      required: true
    },
    videoClass: {
      type: [String, Array, Object],
      default: null
    },
    videoStyle: {
      type: [String, Array, Object],
      default: null
    }
  },

  data: () => ({
    currentTime: 0,
    duration: 0,
    isFullscreen: false,
    isMuted: false,
    isPlaying: false
  }),

  computed: {
    currentTimeLabel() {
      return this.formatTime(this.currentTime)
    },

    durationLabel() {
      return this.formatTime(this.duration)
    },

    volumeIcon() {
      if (this.isMuted) {
        return 'mdi-volume-off'
      }

      return 'mdi-volume-high'
    }
  },

  watch: {
    src() {
      this.currentTime = 0
      this.duration = 0
      this.isPlaying = false
      this.$nextTick(this.syncPlayerState)
    }
  },

  mounted() {
    document.addEventListener('fullscreenchange', this.syncFullscreenState)
  },

  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.syncFullscreenState)
    this.exitFullscreen()
  },

  methods: {
    getVideoElement() {
      return this.$refs.player
    },

    setCurrentTime(currentTime) {
      const player = this.$refs.player

      if (!player) {
        return
      }

      player.currentTime = currentTime
      this.syncPlayerState()
    },

    handleLoadedMetadata(event) {
      this.syncPlayerState()
      this.$emit('loadedmetadata', event)
    },

    handleTimeUpdate(event) {
      this.syncPlayerState()
      this.$emit('timeupdate', event)
    },

    handleVideoClick(event) {
      this.$emit('video-click', event)

      if (event.defaultPrevented) {
        return
      }

      this.togglePlayback()
    },

    syncPlayerState() {
      const player = this.$refs.player

      if (!player) {
        return
      }

      this.currentTime = player.currentTime || 0
      this.duration = Number.isFinite(player.duration) ? player.duration : 0
      this.isMuted = player.muted
      this.isPlaying = !player.paused && !player.ended
    },

    syncFullscreenState() {
      this.isFullscreen = document.fullscreenElement === this.$refs.playerShell
    },

    togglePlayback() {
      const player = this.$refs.player

      if (!player) {
        return
      }

      if (player.paused || player.ended) {
        const playPromise = player.play()

        if (playPromise) {
          playPromise.catch(() => undefined)
        }

        return
      }

      player.pause()
    },

    seek(event) {
      const player = this.$refs.player

      if (!player) {
        return
      }

      player.currentTime = Number(event.target.value)
      this.syncPlayerState()
    },

    toggleMute() {
      const player = this.$refs.player

      if (!player) {
        return
      }

      player.muted = !player.muted
      this.syncPlayerState()
    },

    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen()
        return
      }

      const playerShell = this.$refs.playerShell

      if (playerShell && playerShell.requestFullscreen) {
        const fullscreenPromise = playerShell.requestFullscreen()

        if (fullscreenPromise) {
          fullscreenPromise.catch(() => undefined)
        }
      }
    },

    exitFullscreen() {
      if (document.fullscreenElement === this.$refs.playerShell && document.exitFullscreen) {
        const fullscreenPromise = document.exitFullscreen()

        if (fullscreenPromise) {
          fullscreenPromise.catch(() => undefined)
        }
      }
    },

    formatTime(time) {
      if (!Number.isFinite(time)) {
        return '0:00'
      }

      const minutes = Math.floor(time / 60)
      const seconds = Math.floor(time % 60).toString().padStart(2, '0')

      return `${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.job-approval-video-player {
  background-color: #000;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  max-width: 100%;
  overflow: hidden;
  width: 100%;
}

.job-approval-video-player__video {
  background-color: #000;
  display: block;
  flex: 1 1 auto;
  height: calc(100% - 48px);
  min-height: 0;
  object-fit: contain;
  width: 100%;
}

.job-approval-video-player__controls {
  align-items: center;
  background-color: #000;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  flex: 0 0 48px;
  gap: 8px;
  height: 48px;
  padding: 0 12px;
  position: relative;
  z-index: 20;
}

.job-approval-video-player__control-button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;
}

.job-approval-video-player__control-button .v-icon {
  color: #fff;
}

.job-approval-video-player__seek-control {
  accent-color: #fff;
  cursor: pointer;
  flex: 1 1 auto;
  min-width: 80px;
}

.job-approval-video-player__time-display {
  color: #fff;
  flex: 0 0 auto;
  font-size: 12px;
  line-height: 1;
  min-width: 40px;
  text-align: center;
}

.job-approval-video-player:fullscreen {
  height: 100vh;
  max-height: none;
  max-width: none;
  width: 100vw;
}

.job-approval-video-player:fullscreen .job-approval-video-player__video {
  height: calc(100vh - 56px);
}

.job-approval-video-player:fullscreen .job-approval-video-player__controls {
  flex-basis: 56px;
  height: 56px;
}
</style>
